import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../../Utils/toastContext';
import { useState, useEffect } from 'react';
import { formatDateTimeUTC } from '../../Constants/helper-functions';

/* ------------------------------------------------------------------ */
/*  Presentational style maps (styling only — no logic changes)        */
/* ------------------------------------------------------------------ */

const STATUS_COLORS = {
  NEW: 'bg-blue-500/10 text-blue-700 ring-blue-600/25',
  IN_PROGRESS: 'bg-amber-500/10 text-amber-700 ring-amber-600/30',
  RESOLVED: 'bg-emerald-500/10 text-emerald-700 ring-emerald-600/25',
  CLOSED: 'bg-slate-500/10 text-slate-600 ring-slate-500/25',
};

const STATUS_DOT_COLORS = {
  NEW: 'bg-blue-500',
  IN_PROGRESS: 'bg-amber-500',
  RESOLVED: 'bg-emerald-500',
  CLOSED: 'bg-slate-400',
};

const STATUS_ACCENTS = {
  NEW: 'from-blue-500 via-indigo-500 to-violet-500',
  IN_PROGRESS: 'from-amber-400 via-orange-500 to-rose-400',
  RESOLVED: 'from-emerald-400 via-teal-500 to-cyan-500',
  CLOSED: 'from-slate-300 via-slate-400 to-slate-300',
};

const TICKET_TYPE_STYLE = {
  bug_report: 'bg-rose-500/10 text-rose-700 ring-rose-600/20',
  support_request: 'bg-blue-500/10 text-blue-700 ring-blue-600/20',
  accessibility_issue: 'bg-violet-500/10 text-violet-700 ring-violet-600/20',
  technical_issue: 'bg-emerald-500/10 text-emerald-700 ring-emerald-600/20',
  billing_issue: 'bg-orange-500/10 text-orange-700 ring-orange-600/20',
};

/* ------------------------------------------------------------------ */
/*  Inline SVG icons (no emoji, no extra dependencies)                 */
/* ------------------------------------------------------------------ */

const svgBaseProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

function TicketIcon({ className = 'h-6 w-6' }) {
  return (
    <svg {...svgBaseProps} className={className}>
      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
      <path d="M13 5v2m0 3v2m0 3v2" />
    </svg>
  );
}

function MailIcon({ className = 'h-4 w-4' }) {
  return (
    <svg {...svgBaseProps} className={className}>
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}

function PhoneIcon({ className = 'h-4 w-4' }) {
  return (
    <svg {...svgBaseProps} className={className}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function CalendarIcon({ className = 'h-4 w-4' }) {
  return (
    <svg {...svgBaseProps} className={className}>
      <rect width="18" height="18" x="3" y="4" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

function ClockIcon({ className = 'h-4 w-4' }) {
  return (
    <svg {...svgBaseProps} className={className}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3.5 2" />
    </svg>
  );
}

function RefreshIcon({ className = 'h-4 w-4' }) {
  return (
    <svg {...svgBaseProps} className={className}>
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </svg>
  );
}

function ChevronLeftIcon({ className = 'h-4 w-4' }) {
  return (
    <svg {...svgBaseProps} className={className}>
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function ChevronRightIcon({ className = 'h-4 w-4' }) {
  return (
    <svg {...svgBaseProps} className={className}>
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function InboxIcon({ className = 'h-8 w-8' }) {
  return (
    <svg {...svgBaseProps} className={className}>
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
    </svg>
  );
}

function TagIcon({ className = 'h-3 w-3' }) {
  return (
    <svg {...svgBaseProps} className={className}>
      <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
      <circle cx="7.5" cy="7.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function TicketSkeleton() {
  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-sm">
      <div className="h-1 w-full bg-gradient-to-r from-indigo-200 via-blue-200 to-violet-200" />
      <div className="animate-pulse space-y-4 p-5 sm:p-6">
        <div className="flex items-center justify-between">
          <div className="h-6 w-28 rounded-lg bg-slate-200" />
          <div className="h-7 w-28 rounded-full bg-slate-200" />
        </div>
        <div className="h-6 w-2/3 rounded-lg bg-slate-200" />
        <div className="h-20 rounded-2xl bg-slate-100" />
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="h-16 rounded-2xl bg-slate-100" />
          <div className="h-16 rounded-2xl bg-slate-100" />
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  TicketList — restyled. All data logic below is unchanged.          */
/* ------------------------------------------------------------------ */

export default function TicketList() {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [size] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [data, setData] = useState([]);
  const { showToast } = useToast();
  const navigate = useNavigate();

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return dateStr.split(' ')[0]; // simple formatting
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'NEW':
        return 'bg-blue-50 text-blue-600 border-blue-200';
      case 'IN_PROGRESS':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'RESOLVED':
        return 'bg-green-50 text-green-700 border-green-200';
      default:
        return 'bg-gray-50 text-gray-600 border-gray-200';
    }
  };

  const formatTicketType = (type) => {
    if (!type) return '-';

    return type
      .replace(/_/g, ' ') // bug_report → bug report
      .replace(/\b\w/g, (c) => c.toUpperCase()); // Bug Report
  };

  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };

  const getTickets = async () => {
    try {
      setLoading(true);
      const response = await GetAPIcalls(
        `/api/v1/tickets/?page=${page}&size=10`,
      );

      if (response.status == 200) {
        setData(response.data.tickets);
        setTotalPages(response.data.total_pages);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTickets();
  }, [page]);

  // Derived display-only counts (no behavior change)
  const counts = data.reduce((acc, ticket) => {
    acc[ticket.status] = (acc[ticket.status] || 0) + 1;
    return acc;
  }, {});
  const isInitialLoading = loading && data.length === 0;
  const isPaginating = loading && data.length > 0;

  return (
    <div className="w-full lg:min-w-[78vw] lg:max-w-[80vw] rounded-2xl border border-slate-200/70 bg-gradient-to-b from-white via-white to-slate-50/60 px-4 py-6 shadow-[0_8px_30px_-12px_rgba(15,23,42,0.15)] sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3.5">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-600 via-blue-600 to-violet-600 text-white shadow-lg shadow-indigo-600/25">
            <TicketIcon className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
              My Requests
            </h2>
            <p className="mt-0.5 text-sm text-slate-500">
              Track the status of your support tickets
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-600 shadow-sm">
            <span className="h-1.5 w-1.5 rounded-full bg-indigo-500" />
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={getTickets}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3.5 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshIcon
              className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Status overview strip */}
      {!loading && data.length > 0 && (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: 'Showing', value: data.length, dot: 'bg-indigo-500' },
            { label: 'New', value: counts.NEW || 0, dot: 'bg-blue-500' },
            {
              label: 'In Progress',
              value: counts.IN_PROGRESS || 0,
              dot: 'bg-amber-500',
            },
            {
              label: 'Resolved',
              value: counts.RESOLVED || 0,
              dot: 'bg-emerald-500',
            },
          ].map((stat) => (
            <div
              key={stat.label}
              className="flex items-center gap-3 rounded-2xl border border-slate-200/70 bg-white px-4 py-3 shadow-sm"
            >
              <span
                className={`h-2.5 w-2.5 shrink-0 rounded-full ${stat.dot}`}
              />
              <div className="leading-tight">
                <p className="text-lg font-bold tracking-tight text-slate-900">
                  {stat.value}
                </p>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
                  {stat.label}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Paginating progress shimmer */}
      {isPaginating && (
        <div className="relative mt-6 h-1 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full w-full animate-pulse bg-gradient-to-r from-indigo-500 via-blue-500 to-violet-500" />
        </div>
      )}

      {/* Loading skeletons */}
      {isInitialLoading && (
        <div
          className="mt-6 grid grid-cols-1 gap-5"
          aria-live="polite"
          aria-busy="true"
        >
          <TicketSkeleton />
          <TicketSkeleton />
          <TicketSkeleton />
          <p className="sr-only">Loading requests…</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && data.length === 0 && (
        <div className="mt-6 flex flex-col items-center rounded-3xl border-2 border-dashed border-slate-200 bg-slate-50/60 px-6 py-14 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-gradient-to-br from-indigo-500 to-violet-500 text-white shadow-lg shadow-indigo-500/25">
            <InboxIcon className="h-8 w-8" />
          </div>
          <h3 className="mt-5 text-lg font-bold tracking-tight text-slate-900">
            No tickets found
          </h3>
          <p className="mt-1 max-w-sm text-sm leading-relaxed text-slate-500">
            There are no requests on this page yet. Try going back to the first
            page or check back later.
          </p>
          {page > 1 && (
            <button
              type="button"
              onClick={() => setPage(1)}
              className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-br from-indigo-600 to-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-600/25 transition hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              Back to first page
            </button>
          )}
        </div>
      )}

      {/* Ticket Grid */}
      {data.length > 0 && (
        <div
          className={`mt-6 grid grid-cols-1 gap-5 transition-opacity duration-300 ${
            loading ? 'pointer-events-none opacity-60' : ''
          }`}
        >
          {data.map((ticket) => (
            <article
              key={ticket.ticket_id}
              className="group relative overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-[0_2px_12px_-4px_rgba(15,23,42,0.12)] transition-all duration-300 hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-[0_24px_50px_-20px_rgba(79,70,229,0.35)]"
            >
              {/* Status-driven accent bar */}
              <div
                className={`h-1 w-full bg-gradient-to-r ${
                  STATUS_ACCENTS[ticket.status] || STATUS_ACCENTS.CLOSED
                }`}
              />

              <div className="p-5 sm:p-6">
                {/* Top row: ID + type + status */}
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center rounded-lg bg-slate-900 px-2.5 py-1 font-mono text-xs font-semibold tracking-tight text-white">
                      #{ticket.ticket_id}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ring-1 ring-inset ${
                        TICKET_TYPE_STYLE[ticket.ticket_type] ||
                        'bg-slate-500/10 text-slate-600 ring-slate-500/20'
                      }`}
                    >
                      <TagIcon className="h-3 w-3" />
                      {formatTicketType(ticket.ticket_type)}
                    </span>
                  </div>

                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ring-1 ring-inset ${
                      STATUS_COLORS[ticket.status] || STATUS_COLORS.CLOSED
                    }`}
                  >
                    <span className="relative flex h-2 w-2">
                      {ticket.status === 'IN_PROGRESS' && (
                        <span
                          className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${
                            STATUS_DOT_COLORS[ticket.status] || 'bg-slate-400'
                          }`}
                        />
                      )}
                      <span
                        className={`relative inline-flex h-2 w-2 rounded-full ${
                          STATUS_DOT_COLORS[ticket.status] || 'bg-slate-400'
                        }`}
                      />
                    </span>
                    {ticket.status?.replace(/_/g, ' ')}
                  </span>
                </div>

                {/* Subject */}
                <h3 className="mt-4 text-lg font-bold leading-snug tracking-tight text-slate-900 transition-colors group-hover:text-indigo-950 sm:text-xl">
                  {ticket.subject}
                </h3>

                {/* Description */}
                <div className="mt-3 rounded-2xl border border-slate-100 bg-slate-50/80 p-4">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
                    Description
                  </p>
                  <p className="mt-1.5 line-clamp-3 text-sm leading-relaxed text-slate-600">
                    {ticket.content}
                  </p>
                </div>

                {/* Contact info */}
                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex min-w-0 items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50/70 p-3.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-500/10 text-blue-600">
                      <MailIcon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[11px] font-bold uppercase tracking-widest text-slate-400">
                        Email
                      </span>
                      <span className="mt-0.5 block break-all text-sm font-semibold text-slate-800">
                        {ticket.requester_email_address}
                      </span>
                    </span>
                  </div>

                  <div className="flex min-w-0 items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50/70 p-3.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-500/10 text-violet-600">
                      <PhoneIcon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[11px] font-bold uppercase tracking-widest text-slate-400">
                        Phone
                      </span>
                      <span className="mt-0.5 block break-all text-sm font-semibold text-slate-800">
                        {ticket.requester_phone_number}
                      </span>
                    </span>
                  </div>
                </div>

                {/* Footer: timestamps */}
                <div className="mt-5 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-slate-100 pt-4 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarIcon className="h-3.5 w-3.5 text-emerald-500" />
                    Created
                    <span className="font-semibold text-slate-700">
                      {formatDateTimeUTC(ticket.date_created)}
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <ClockIcon className="h-3.5 w-3.5 text-indigo-500" />
                    Updated
                    <span className="font-semibold text-slate-700">
                      {formatDateTimeUTC(ticket.date_updated)}
                    </span>
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Pagination */}
      {data.length > 0 && (
        <nav
          aria-label="Ticket pages"
          className="mt-8 flex flex-col items-center gap-3"
        >
          <div className="flex flex-wrap items-center justify-center gap-1.5 rounded-2xl border border-slate-200/70 bg-white px-2.5 py-2 shadow-sm">
            <button
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
              disabled={page === 1}
              aria-label="Previous page"
              className="inline-flex h-9 items-center gap-1 rounded-xl px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Prev</span>
            </button>

            <span
              className="mx-1 hidden h-6 w-px bg-slate-200 sm:block"
              aria-hidden="true"
            />

            {[...Array(totalPages)].map((_, idx) => {
              const pageNum = idx + 1;
              const isActive = page === pageNum;

              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  aria-current={isActive ? 'page' : undefined}
                  aria-label={`Go to page ${pageNum}`}
                  className={`h-9 min-w-[2.25rem] rounded-xl px-3 text-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${
                    isActive
                      ? 'bg-gradient-to-br from-indigo-600 to-blue-600 font-bold text-white shadow-md shadow-indigo-600/25 hover:brightness-110'
                      : 'font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            <span
              className="mx-1 hidden h-6 w-px bg-slate-200 sm:block"
              aria-hidden="true"
            />

            <button
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              disabled={page === totalPages}
              aria-label="Next page"
              className="inline-flex h-9 items-center gap-1 rounded-xl px-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>

          <p className="text-xs font-medium text-slate-400">
            Showing page{' '}
            <span className="font-bold text-slate-600">{page}</span> of{' '}
            <span className="font-bold text-slate-600">{totalPages}</span>
          </p>
        </nav>
      )}
    </div>
  );
}
